import * as React from 'react';
import {Query} from 'react-apollo';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import _ from 'underscore';

// @ts-expect-error
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {InfoBanner} from 'shells/info-banner/info-banner';
import {ListViewScrollable} from 'shells/list-view/list-view-scrollable';
import type {Row} from 'shells/list-view/list-view-scrollable';

import type {
    ContactSearchResult as ContactSearchResultFragment,
    AccountSearchResult as AccountSearchResultFragment,
    LeadSearchResult as LeadSearchResultFragment,
    GetSearchResults as GetSearchResultsQuery,
    GetSearchResultsVariables as GetSearchResultsQueryVariables,
} from 'nutshell-graphql-types';

import {SearchResultsEmptyView} from '../core/search/empty-view';

import GET_SEARCH_RESULTS from './graphql/queries/get-search-results.graphql';
import {SearchResult} from './search-result';

import styles from './search-results.module.css';

type OwnProps = {
    query: string;
    // Since we want our query to be debounced, the `query` prop being
    // passed in only updates about 250ms, however, we want to block searching
    // while typing, so we fake it from the parent on every input change
    isFakeSearching: boolean;
    closeSidebar: () => void;
};

type RowItem = {
    render: (arg1: boolean) => React.ReactElement<any>;
    onSelect: () => void;
};

type GraphQLProps = {
    isLoading: boolean;
    topResults:
        | ContactSearchResultFragment
        | AccountSearchResultFragment
        | LeadSearchResultFragment[];
    recentResults: Array<
        ContactSearchResultFragment | AccountSearchResultFragment | LeadSearchResultFragment
    >;
    leadResults: LeadSearchResultFragment[];
    contactResults: ContactSearchResultFragment[];
    accountResults: AccountSearchResultFragment[];
};

type Props = OwnProps & GraphQLProps & ContextRouter;

export class SearchResultsComponent extends React.PureComponent<Props> {
    render() {
        if (this.props.isLoading || this.props.isFakeSearching) {
            return (
                <div className={styles.results}>
                    <div className={styles.loading}>
                        <div>Searching…</div>
                        <LoadingIcon className={styles['loading-icon']} size={40} />
                    </div>
                </div>
            );
        }

        if (this.props.query && !this.props.topResults.length) {
            return (
                <div className={styles.results}>
                    <SearchResultsEmptyView hasQuery={true} />
                </div>
            );
        }

        if (!this.props.query && !this.props.recentResults.length) {
            return (
                <div className={styles.results}>
                    <SearchResultsEmptyView hasQuery={false} />
                </div>
            );
        }

        const collection = this.getSearchResultsCollection();

        return (
            <div className={styles.results}>
                <div className={styles['recently-viewed-container']}>
                    <div className={styles['recently-viewed']}>
                        <ListViewScrollable
                            collection={collection}
                            renderRow={(item, isSelected) => {
                                return item.render(isSelected);
                            }}
                            onSelect={(item?: RowItem | null) => {
                                if (item) {
                                    item.onSelect();
                                } else {
                                    const firstItem = _.find(collection, (row: Row) => {
                                        return row.shouldDisableSelection === false;
                                    });
                                    if (firstItem && firstItem.rowItem.onSelect) {
                                        firstItem.rowItem.onSelect();
                                    }
                                }
                            }}
                            shouldfadeBottom={false}
                        />
                    </div>
                    <InfoBanner>
                        <div className={styles['shortcut-text']}>
                            Did you know? Use the <span className={styles.slash}>/</span> key to
                            quickly pull up Nutshell search at any time!
                        </div>
                    </InfoBanner>
                </div>
            </div>
        );
    }

    getSearchResultsCollection = (): Row[] => {
        // @ts-expect-error
        let collection = [];

        if (!this.props.query && this.props.recentResults.length) {
            collection = [
                {
                    shouldDisableSelection: true,
                    rowItem: {
                        render: () => (
                            <div
                                key='recently-viewed-header'
                                className={styles['result-entity-header']}
                            >
                                Recently viewed
                            </div>
                        ),
                    },
                },
            ];
            collection = collection.concat(
                // @ts-expect-error
                this.props.recentResults.map((searchResult) => {
                    return {
                        shouldDisableSelection: false,
                        rowItem: this.getSearchResultRowItem(searchResult, 'recent'),
                    };
                })
            );
        } else {
            if (this.props.topResults.length) {
                collection.push({
                    shouldDisableSelection: true,
                    rowItem: {
                        render: () => (
                            <div
                                key='top-results-header'
                                className={styles['result-entity-header']}
                            >
                                Top results
                            </div>
                        ),
                    },
                });
                collection = collection.concat(
                    // @ts-expect-error
                    this.props.topResults.map((searchResult) => {
                        return {
                            shouldDisableSelection: false,
                            rowItem: this.getSearchResultRowItem(searchResult, 'top'),
                        };
                    })
                );
            }

            if (this.props.leadResults.length) {
                collection.push({
                    shouldDisableSelection: true,
                    rowItem: {
                        render: () => (
                            <div
                                key='lead-results-header'
                                className={styles['result-entity-header']}
                            >
                                Leads
                            </div>
                        ),
                    },
                });
                // @ts-expect-error
                collection = collection.concat(
                    // @ts-expect-error
                    this.props.leadResults.map((searchResult) => {
                        return {
                            shouldDisableSelection: false,
                            rowItem: this.getSearchResultRowItem(searchResult, 'leads'),
                        };
                    })
                );
                collection.push({
                    shouldDisableSelection: false,
                    rowItem: this.getMoreResultsLink(
                        `/leads?q=${this.props.query}&view=list`,
                        'Search all leads'
                    ),
                });
            }

            if (this.props.contactResults.length) {
                collection.push({
                    shouldDisableSelection: true,
                    rowItem: {
                        render: () => (
                            <div
                                key='contact-result-header'
                                className={styles['result-entity-header']}
                            >
                                People
                            </div>
                        ),
                    },
                });
                collection = collection.concat(
                    // @ts-expect-error
                    this.props.contactResults.map((searchResult) => {
                        return {
                            shouldDisableSelection: false,
                            rowItem: this.getSearchResultRowItem(searchResult, 'contacts'),
                        };
                    })
                );
                collection.push({
                    shouldDisableSelection: false,
                    rowItem: this.getMoreResultsLink(
                        `/people?q=${this.props.query}&view=list`,
                        'Search all people'
                    ),
                });
            }

            if (this.props.accountResults.length) {
                collection.push({
                    shouldDisableSelection: true,
                    rowItem: {
                        render: () => (
                            <div
                                key='account-result-header'
                                className={styles['result-entity-header']}
                            >
                                Companies
                            </div>
                        ),
                    },
                });
                collection = collection.concat(
                    // @ts-expect-error
                    this.props.accountResults.map((searchResult) => {
                        return {
                            shouldDisableSelection: false,
                            rowItem: this.getSearchResultRowItem(searchResult, 'accounts'),
                        };
                    })
                );
                collection.push({
                    shouldDisableSelection: false,
                    rowItem: this.getMoreResultsLink(
                        `/companies?q=${this.props.query}&view=list`,
                        'Search all companies'
                    ),
                });
            }
        }

        return collection;
    };

    getSearchResultRowItem = (
        searchResult:
            | ContactSearchResultFragment
            | AccountSearchResultFragment
            | LeadSearchResultFragment,
        section: string
    ): RowItem => {
        return {
            render: (isSelected: boolean) => (
                <SearchResult
                    key={`${section}-${searchResult.id}`}
                    searchResult={searchResult}
                    isActive={isSelected}
                    closeSidebar={this.props.closeSidebar}
                />
            ),
            onSelect: () => {
                this.props.closeSidebar();
                this.props.history.push(searchResult.htmlUrlPath);
            },
        };
    };

    getMoreResultsLink = (href: string, title: string) => {
        return {
            render: (isSelected: boolean) => (
                <a
                    key={title}
                    href={href}
                    className={
                        isSelected
                            ? styles['list-item-more-link--fake-focus']
                            : styles['list-item-more-link']
                    }
                >
                    {title}
                </a>
            ),
            onSelect: () => {
                this.props.closeSidebar();
                this.props.history.push(href);
            },
        };
    };
}

class SearchResultsQuery extends Query<GetSearchResultsQuery, GetSearchResultsQueryVariables> {}

// @ts-expect-error
export const SearchResults = withRouter<OwnProps>((props: OwnProps & ContextRouter) => {
    return (
        <SearchResultsQuery
            query={GET_SEARCH_RESULTS}
            variables={{query: props.query}}
            fetchPolicy='no-cache'
        >
            {({data, loading}) => {
                const search = data ? data.search : null;
                const quickResults = search ? search.quickResults : null;

                return (
                    <SearchResultsComponent
                        {...props}
                        isLoading={loading}
                        leadResults={(quickResults && quickResults.leadResults) || []}
                        contactResults={(quickResults && quickResults.contactResults) || []}
                        accountResults={(quickResults && quickResults.accountResults) || []}
                        recentResults={(quickResults && quickResults.recentResults) || []}
                        topResults={(quickResults && quickResults.topResults) || []}
                    />
                );
            }}
        </SearchResultsQuery>
    );
});
